import { useEffect, useRef, useState } from 'react'

const TRUSTPILOT_BOOTSTRAP_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'

/** Official TrustBox IDs from Trustpilot Review Collector (primedocumentsign.com). */
const TRUSTPILOT_TEMPLATE_ID = '56278e9afbbba0bdcd568bc'
const TRUSTPILOT_BUSINESS_UNIT_ID = '69d797522fc578613b885f50'
const TRUSTPILOT_WIDGET_TOKEN = '7af771e3-c1f4-4f4f-8ed2-cc2c6b824618'

function ensureTrustpilotScript(onLoad) {
  if (typeof document === 'undefined') return () => {}
  const existing = document.querySelector(`script[src="${TRUSTPILOT_BOOTSTRAP_SRC}"]`)
  if (existing) {
    if (window.Trustpilot) {
      onLoad()
      return () => {}
    }
    existing.addEventListener('load', onLoad)
    return () => existing.removeEventListener('load', onLoad)
  }
  const script = document.createElement('script')
  script.src = TRUSTPILOT_BOOTSTRAP_SRC
  script.async = true
  script.addEventListener('load', onLoad)
  document.head.appendChild(script)
  return () => script.removeEventListener('load', onLoad)
}

/**
 * Trustpilot TrustBox — loads bootstrap once globally; initializes when scrolled into view.
 */
export function TrustpilotWidget({ className = '' }) {
  const wrapRef = useRef(null)
  const widgetRef = useRef(null)
  const [visible, setVisible] = useState(false)
  const [scriptReady, setScriptReady] = useState(false)

  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return undefined
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '120px', threshold: 0.01 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!visible) return undefined
    return ensureTrustpilotScript(() => setScriptReady(true))
  }, [visible])

  useEffect(() => {
    if (!scriptReady || !widgetRef.current || typeof window === 'undefined') return
    const tp = window.Trustpilot
    if (typeof tp?.loadFromElement === 'function') {
      try {
        tp.loadFromElement(widgetRef.current, true)
      } catch (_) {
        /* Trustpilot may still auto-scan */
      }
    }
  }, [scriptReady])

  return (
    <div
      ref={wrapRef}
      className={`trustpilot-widget-embed${className ? ` ${className}` : ''}`.trim()}
      aria-label="Trustpilot reviews"
    >
      <div
        ref={widgetRef}
        className="trustpilot-widget"
        data-locale="en-US"
        data-template-id={TRUSTPILOT_TEMPLATE_ID}
        data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
        data-style-height="52px"
        data-style-width="100%"
        data-token={TRUSTPILOT_WIDGET_TOKEN}
      >
        <a href="https://www.trustpilot.com/review/primedocumentsign.com" target="_blank" rel="noopener noreferrer">
          Trustpilot
        </a>
      </div>
    </div>
  )
}
